import {
  getPrimaryProjectLocation,
  Impact,
  isTree,
  pluralise,
  PublicProjectSummary,
  TileVariant,
  toLocaleFormattedDate,
  User,
} from "@ecologi/shared";
import isEmpty from "lodash/isEmpty";
import startCase from "lodash/startCase";
import { projectSubTypeVisuals, projectTypeVisuals } from "../../consts";
import { getResizedImageURL } from "../../utils";
import { COUNTRY_ICONS, SAPLING_ICON_URL } from "../consts";

export const getImpactDate = (impact: Impact) => {
  const date =
    isTree(impact) && impact.plantedOnDate
      ? impact.plantedOnDate
      : impact.createdAt;

  return toLocaleFormattedDate(date);
};

export const getFundedBy = (user: User | null, impact?: Impact) => {
  // If the tile has a custom name, it should be used
  if (impact?.name && !isEmpty(impact.name.trim())) return impact.name;

  if (!user) return null;

  // If there is a group name for family or business users
  if (
    (user.isFamilyPlan || user.isBusinessType) &&
    !isEmpty(user.groupName.trim())
  )
    return user.groupName;

  // Don't expose first name and last name for business users
  if (!user.isBusinessType) {
    // Individual and family users
    const fullName = `${user.firstName} ${user.lastName}`;
    if (!isEmpty(fullName.trim())) return fullName;
  }

  // Return username if all else fails
  return user.username;
};

export const getLocationAndProjectLabel = (
  impact: Impact,
  project: PublicProjectSummary
) => {
  const primaryLocation = getPrimaryProjectLocation(project);
  const region = primaryLocation?.region;
  const country = primaryLocation?.country;

  if (isTree(impact)) {
    if (impact.plantedBy) {
      return [region || country, startCase(impact.plantedBy.toLowerCase())];
    }
    if (region) {
      return [country, region];
    }
  }
  return [country || "", "Read more about this project"];
};

export const getPictogramUrl = (impact: Impact): string => {
  if (isTree(impact) && impact.localProject) {
    return COUNTRY_ICONS[impact.localProject?.country].pictogram;
  }

  if (!isTree(impact)) {
    const project = impact.project;

    if (!project) return SAPLING_ICON_URL;

    return project.projectSubType
      ? projectSubTypeVisuals[project.projectSubType].pictogram
      : projectTypeVisuals[project.projectType].pictogram;
  }

  return SAPLING_ICON_URL;
};

export const getTitle = (impact: Impact): string => {
  if (isTree(impact)) {
    if (impact.isBirthday) return "Ecologi Birthday Tree 🎉";

    if (impact.localProject) {
      return `${pluralise(impact.value, "local tree")} funded`;
    }

    if (impact.isDividend) {
      return `${impact.value.toLocaleString()} Dividend trees funded`;
    }

    return `${impact.value.toLocaleString()}${
      impact.variant === TileVariant.REFERRAL ? " bonus referral" : ""
    } ${pluralise(impact.value, "tree", false)} funded`;
  }

  return impact.project?.title || "";
};

export const getModalImagePath = (
  impact: Impact,
  project?: PublicProjectSummary,
  isSubProfile?: boolean
): string => {
  if (isSubProfile) return "/images/app/subProfileModal.svg";

  if (isTree(impact)) {
    if (impact.isDividend || impact.isBirthday) {
      return "/images/app/tree-present.jpg";
    }

    const projectImageUrl =
      getResizedImageURL(
        project?.primaryImage?.imageKey || project?.images[0]?.imageKey || "",
        640
      ) || "/images/app/tree-planter.jpg";

    return projectImageUrl;
  }

  if (project?.images.length) {
    const imgUrl = project.images[0]?.imageKey || "";

    return `${getResizedImageURL(imgUrl, 640)}`;
  }

  return project?.primaryImage?.imageKey || project?.images[0]?.imageKey || "";
};
